<template>
  <!-- Inline, so the cell keeps centring the icon the way it did when the menu was
       the whole of this component. -->
  <span
    class="row-actions"
    @mouseenter="mountMenu"
  >
    <v-menu
      v-if="isMenuMounted"
      v-model="isMenuOpen"
      offset-y
      offset-overflow
      left
      bottom
      :close-on-content-click="closeActionsMenuOnClick"
      content-class="actions-menu"
    >
      <template
        #activator="{ on, attrs }"
      >
        <v-icon
          v-bind="attrs"
          :disabled="disabled"
          v-on="on"
        >
          mdi-dots-vertical
        </v-icon>
      </template>

      <v-list
        style="padding: 8px;"
      >
        <template
          v-for="(action, actionIndex) in actionsConfig"
        >
          <div
            v-if="canShowAction(action)"
            :key="actionIndex"
            :style="getActionStyle(action)"
          >
            <v-list-item
              class="d-flex align-center list-item"
              :style="getActionStyle(action)"
              link
              :disabled="getActionDisabled(action)"
              @click="onActionClick(action, dataIndex)"
            >
              <v-list-item-icon
                class="align-self-center my-0 mr-1 list-icon"
              >
                <!--              action icon-->
                <v-icon
                  v-if="!action.src"
                  class="list-icon"
                >
                  {{ getActionIcon(action) }}
                </v-icon>
                <!--              action image-->
                <image-field
                  v-else
                  :src="getActionSrc(action)"
                  :width="action.width || 17"
                  :height="action.height || 17"
                  :contain="action.contain || true"
                />
              </v-list-item-icon>

              <!--              action label-->
              <v-list-item-title
                class="actions-list-item"
              >
                {{ getActionLabel(action) }}
              </v-list-item-title>
            </v-list-item>
          </div>
        </template>
      </v-list>
    </v-menu>

    <!-- What the row shows until it is reached for: the menu's own icon, and nothing
         behind it. See `mountMenu` for why the menu is not there from the start. -->
    <v-icon
      v-else
      :disabled="disabled"
      @click="openMenu"
    >
      mdi-dots-vertical
    </v-icon>
  </span>
</template>

<script>
import { isObject } from 'lodash'
import ImageField from '@/global/components/fields/ImageField.vue'
import { isFunction } from '@/global/utils/utils'

export default {
  name: 'TableRowActionsMenu',

  components: {
    ImageField
  },

  props: {
    config: {
      type: Object,
      required: true,
      default: () => ({})
    },
    dataIndex: {
      type: Number,
      required: true,
      default: 0
    },
    tableData: {
      type: Array,
      default: () => ([])
    },
    permissions: {
      type: Object,
      default: null
    },
    fetchTableData: {
      type: Function,
      default: () => {}
    },
    resetSelections: {
      type: Function,
      default: () => {}
    },
    pagination: {
      type: Object,
      default: () => ({})
    },
    showTableDetailsPage: {
      type: Function,
      default: () => {}
    }
  },

  data () {
    return {
      isMenuMounted: false,
      isMenuOpen: false
    }
  },

  computed: {
    tableConfig () {
      return this.config?.tableConfig
    },

    actionsConfig () {
      return this.tableConfig?.actionsConfig || []
    },

    disabled () {
      const { disabled = false } = this.tableConfig || {}
      let result = disabled

      if (isFunction(disabled)) {
        result = disabled({
          index: this.dataIndex,
          data: this.tableData
        })
      }

      // check actions visibility
      const allHidden = this.actionsConfig.every(action => !this.canShowAction(action))
      return result || allHidden
    },

    closeActionsMenuOnClick () {
      return this.tableConfig?.closeActionsMenuOnClick === true
    }
  },

  methods: {
    /**
     * Stands the menu up the first time the row is reached for.
     *
     * A menu is a real component - an overlay, its own listeners, a list it can show -
     * and a listing showing every record at once holds one per row. A few thousand of
     * them cost seconds to put up and are paid for again on every redraw, while all but
     * the one being clicked are never opened. So the row shows the icon alone until the
     * cursor reaches it, and the menu itself arrives just before it is needed.
     */
    mountMenu () {
      this.isMenuMounted = true
    },

    /**
     * The click that arrives without a hover before it - a touch screen. The menu is
     * put up and then opened, because the click that asked for it landed on the icon
     * that stood in for it and cannot be passed on.
     */
    openMenu () {
      if (this.disabled) {
        return
      }

      this.isMenuMounted = true
      this.$nextTick(() => {
        this.isMenuOpen = true
      })
    },

    onActionClick (action) {
      const { click } = action
      if (click && typeof click === 'function') {
        click({
          index: this.dataIndex,
          data: this.tableData[this.dataIndex],
          instance: this.$parent?.$parent?.$parent,
          pagination: this.pagination,
          showTableDetailsPage: this.showTableDetailsPage,
          fetchTableData: this.fetchTableData,
          resetSelections: this.resetSelections
        })
      }
    },

    getActionDisabled (action) {
      const { disabled } = action || {}
      let disabledValue = disabled

      if (disabled === undefined) {
        return false
      }

      if (typeof disabled === 'function') {
        disabledValue = disabled({
          permissions: this.permissions,
          data: this.tableData.length ? this.tableData[this.dataIndex] : []
        })
      }

      return !!disabledValue
    },

    canShowAction (action) {
      const { visible = true } = action || {}
      let visibleValue = visible

      if (isFunction(visible)) {
        visibleValue = visible({
          permissions: this.permissions,
          data: this.tableData.length ? this.tableData[this.dataIndex] : []
        })
      }

      return !!visibleValue
    },

    getActionSrc (action) {
      const { src } = action || {}
      let srcVal

      if (src && typeof src === 'string') {
        return src
      }
      else if (typeof src === 'function') {
        srcVal = src({
          data: this.tableData[this.dataIndex]
        })
      }

      return srcVal && typeof srcVal === 'string' ? srcVal : undefined
    },

    getActionStyle (action) {
      const { style } = action || {}
      let styleValue = {
        borderRadius: '16px'
      }

      const isActionDisabled = this.getActionDisabled(action)

      if (isActionDisabled) {
        styleValue.backgroundColor = 'rgba(35,34,34,0.12)'
        styleValue.cursor = 'not-allowed'
      }

      if (style && isObject(style)) {
        styleValue = {
          ...styleValue,
          ...style
        }
      }

      return styleValue
    },

    getActionIcon (action) {
      if (!this.tableData || this.dataIndex === undefined) {
        return ''
      }

      const { icon } = action || {}
      let returnVal = icon

      if (typeof icon === 'function') {
        returnVal = icon({
          data: this.tableData[this.dataIndex]
        })
      }

      return returnVal && typeof returnVal === 'string' ? returnVal : ''
    },

    getActionLabel (action) {
      if (!this.tableData || this.dataIndex === undefined) {
        return ''
      }

      const { label } = action || {}
      let returnVal = label

      if (typeof label === 'function') {
        returnVal = label({
          data: this.tableData[this.dataIndex]
        })
      }

      return returnVal && typeof returnVal === 'string' ? returnVal : ''
    }
  }
}
</script>

<style scoped>
.row-actions {
  display: inline;
}

.actions-menu {
  border-radius: 16px;
}

.actions-list-item {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  font-size: 14px !important;
  line-height: 32px !important;
  color: #111827 !important;
}

.list-icon {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  width: 17px !important;
  height: 17px !important;
  line-height: 32px !important;
  color: #6B7280 !important;
  pointer-events: auto;
  min-width: unset !important;
  font-size: 18px;
}

.list-item:hover:before {
  border-radius: 16px !important;
}
</style>
